import React, { useEffect, useState } from 'react';

import { toast } from 'react-toastify';
import api from 'services/api';
import { PageList, ServiceType } from 'types/common';

import { Stepper } from 'shared/components/molecules/Stepper/Stepper';
import { normalizeBornDate, sanitizeString } from 'utils/format';

import { FirstRegisterGroup } from './components/FirstRegisterGroup/FirstRegisterGroup';
import { SecondRegisterGroup } from './components/SecondRegisterGroup/SecondRegisterGroup';
import { ThirdRegisterGroup } from './components/ThirdRegisterGroup/ThirdRegisterGroup';
import { FourthRegisterGroup } from './components/FourthRegisterGroup/FourthRegisterGroup';

import person from 'assets/icons/person.svg';
import filled_person from 'assets/icons/filled_person.svg';
import man from 'assets/icons/man.svg';
import filled_man from 'assets/icons/filled_man.svg';
import checked_coin from 'assets/icons/checked_coin.svg';
import filled_checked_coin from 'assets/icons/filled_checked_coin.svg';

import {
  Container,
  FieldsWrapper,
  LeftBottom,
  LeftCenter,
  LeftTop,
  RegisterWrapper,
  RepfyLogo,
  RightBottom,
  RightTop,
} from './_register';

export interface OptionValues {
  label: string;
  value: string;
}

export const Register: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [name, setName] = useState<string>('');
  const [bornDate, setBornDate] = useState<string>('');
  const [cpf, setCpf] = useState<string>('');
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [category, setCategory] = useState<OptionValues>({
    label: '',
    value: '',
  });
  const [cnpj, setCnpj] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [passwordConfirmation, setPasswordConfirmation] = useState<string>('');
  const [agreedToPolicy, setAgreedToPolicy] = useState<boolean>(false);
  const [challengeCode, setChallengeCode] = useState<string>('');

  const [showTerms, setShowTerms] = useState<boolean>(false);
  const [showValidationModal, setShowValidationModal] =
    useState<boolean>(false);

  const [loading, setLoading] = useState<boolean>(false);

  const [categoryOptions, setCategoryOptions] = useState<OptionValues[]>([]);

  useEffect(() => {
    getOccupationOptions();
  }, []);

  useEffect(() => {
    checkIfIsConfirming();
  }, []);

  // When an user with unconfirmed email tries to log in
  // they will be redirected to /cadastro from login page with a '?confirmOnly=true' query param
  const checkIfIsConfirming = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const confirmOnly = urlParams.get('confirmOnly');
    if (confirmOnly) {
      setCurrentStep(2);
    }
  }

  const sendEmailVerification = async () => {
    setCurrentStep(2);
    setLoading(true);

    const emailToSend = {
      email: email.trim(),
    };

    try {
      await api.post(`users/send-verification`, emailToSend);

      setCurrentStep(2);
    } catch (error: any) {
      handleError(error.response?.data?.message || error.toString());
    } finally {
      setLoading(false);
    }
  };

  const checkChallengeCode = async () => {
    setLoading(true);

    const code = {
      code: challengeCode,
    };

    try {
      await api.post(`users/verify`, code);

      setShowValidationModal(true);
    } catch (error: any) {
      handleError(error.response?.data?.message || error.toString());
    } finally {
      setLoading(false);
    }
  };

  const getOccupationOptions = async () => {
    setLoading(true);
    try {
      const response = await api.get<PageList<ServiceType>>(
        `service-types?limit=99999`,
      );

      if (response) {
        const categories = response.data.results.filter(category => category?.is_active);

        categories.forEach((item) => {
          const option = {
            label: item.name,
            value: String(item.id),
          };

          setCategoryOptions((prevState) => [...prevState, option]);
        });
      }
    } catch (error: any) {
      handleError(error.response?.data?.message || error.toString());
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    setLoading(true);

    const userData = {
      email: email.trim(),
      password: password,
      password_confirmation: passwordConfirmation,
      individual_person: {
        name: name,
        cpf: cpf,
        born_at: normalizeBornDate(bornDate),
      },
      user_phone: {
        country_code: '+55',
        state_code: phoneNumber.slice(0, 2),
        number: phoneNumber.slice(2, phoneNumber.length - 1),
      },
      legal_person: {
        cnpj: sanitizeString(cnpj) || undefined,
      },
      provider: {
        services_types_ids: [category.value],
      },
    };

    try {
      await api.post(`users`, userData);

      sendEmailVerification();
      setCurrentStep(2);
    } catch (error: any) {
      handleError(error.response?.data?.message || error.toString());
    } finally {
      setLoading(false);
    }
  };

  const handleError = (error: string) => {
    toast.error(error);
  };

  const steps = [
    {
      step: 0,
      activeImage: filled_person,
      inactiveImage: person,
    },
    {
      step: 1,
      activeImage: filled_man,
      inactiveImage: man,
    },
    {
      step: 2,
      activeImage: filled_checked_coin,
      inactiveImage: checked_coin,
    },
  ];

  const renderInputGroup = (step: number) => {
    const inputGroup: { [key: number]: JSX.Element } = {
      0: (
        <FirstRegisterGroup
          name={name}
          setName={setName}
          cpf={cpf}
          setCpf={setCpf}
          phoneNumber={phoneNumber}
          setPhoneNumber={setPhoneNumber}
          setCurrentStep={setCurrentStep}
          bornDate={bornDate}
          setBornDate={setBornDate}
        />
      ),
      1: (
        <SecondRegisterGroup
          category={category}
          setCategory={setCategory}
          categoryOptions={categoryOptions}
          cnpj={cnpj}
          setCnpj={setCnpj}
          setCurrentStep={setCurrentStep}
        />
      ),
      1.1: (
        <ThirdRegisterGroup
          email={email}
          setEmail={setEmail}
          password={password}
          setPassword={setPassword}
          passwordConfirmation={passwordConfirmation}
          setPasswordConfirmation={setPasswordConfirmation}
          agreedToPolicy={agreedToPolicy}
          setAgreedToPolicy={setAgreedToPolicy}
          showTerms={showTerms}
          setShowTerms={setShowTerms}
          handleCreateUser={handleCreateUser}
          setCurrentStep={setCurrentStep}
        />
      ),
      2: (
        <FourthRegisterGroup
          challengeCode={challengeCode}
          setChallengeCode={setChallengeCode}
          loading={loading}
          showValidationModal={showValidationModal}
          setShowValidationModal={setShowValidationModal}
          setCurrentStep={setCurrentStep}
          checkChallengeCode={checkChallengeCode}
        />
      ),
    };

    return inputGroup[step];
  };
  return (
    <Container>
      <LeftTop />

      <LeftCenter />

      <LeftBottom />

      <RegisterWrapper>
        <RepfyLogo />

        <h1>Prestadores</h1>

        <FieldsWrapper>
          <div className="fields-wrapper__info">
            <span className="fields-wrapper__info__title">
              Informações pessoais
            </span>
          </div>

          <Stepper
            steps={steps}
            currentStep={currentStep}
            inactiveColor={'#B3E5F3'}
            activeColor={'var(--blue12)'}
            setSteps={setCurrentStep}
          />

          {renderInputGroup(currentStep)}
        </FieldsWrapper>
      </RegisterWrapper>

      <RightTop />

      <RightBottom />
    </Container>
  );
};
